/**
 * The caller's own network participant registrations.
 *
 * An offering names its lead by `leadParticipantId`, a CrtParticipant id, and
 * an indication of interest names its submitter by `participantId`, also a
 * CrtParticipant id. Neither is a tenant id. OfferingDetail compared the
 * user's tenantId with leadParticipantId, which never matches, so a lead was
 * offered "Indicate Interest" on its own offering, and it posted the tenantId
 * as the IOI's participantId (2026-09-27 sweep, seen beside fi-g9-12).
 *
 * GET /api/v1/crt/network/participants is tenant-scoped server-side
 * (resolveEffectiveTenantId), so the list it returns IS the caller's
 * registrations. List a Loan reads the same list for its create body's
 * leadParticipantId.
 */

import { useQuery } from '@tanstack/react-query';
import { cooperativeReserveTokenClient } from '../../api/client';

export function useOwnParticipants() {
  return useQuery({
    queryKey: ['crt-own-participants', 'ACTIVE'],
    queryFn: async () => {
      const { data, error } = await cooperativeReserveTokenClient.GET(
        '/api/v1/crt/network/participants',
        { params: { query: { status: 'ACTIVE', page: 1, pageSize: 25 } } },
      );
      if (error) {
        throw new Error(
          (error as { message?: string })?.message
          ?? 'Failed to load your network participant registration',
        );
      }
      return data;
    },
    staleTime: 60 * 1000,
    retry: false,
  });
}

/**
 * How the caller's registrations resolve to one participant for an action
 * taken in the institution's name.
 *
 *   loading    the read has not finished
 *   failed     the read failed, so the registration is unknown
 *   none       the institution has no active registration
 *   ambiguous  more than one active registration; the server refuses to pick
 *   ready      exactly one, carried as `participantId`
 */
export type OwnParticipantState =
  | { kind: 'loading' }
  | { kind: 'failed' }
  | { kind: 'none' }
  | { kind: 'ambiguous'; count: number }
  | { kind: 'ready'; participantId: string };

export function resolveOwnParticipant(read: {
  isLoading: boolean;
  isError: boolean;
  participantIds: readonly string[] | undefined;
}): OwnParticipantState {
  if (read.isError) return { kind: 'failed' };
  if (read.isLoading || !read.participantIds) return { kind: 'loading' };
  if (read.participantIds.length === 0) return { kind: 'none' };
  if (read.participantIds.length > 1) {
    return { kind: 'ambiguous', count: read.participantIds.length };
  }
  return { kind: 'ready', participantId: read.participantIds[0] };
}
